import { Modal, message } from "antd";
import { useSelector } from "react-redux";
import { useState } from "react";
import "./shareModal.scss";
import facebookIcon from "../../../assets/icons/facebook-logo.svg";
import twitterIcon from "../../../assets/icons/twitter.svg";
import instagramIcon from "../../../assets/icons/instagram.svg";
import starIcon from "../../../assets/icons/star-blue.svg";
import shareIcon from "../../../assets/icons/shareIcon.svg";
import { shareCertificateRequest } from "../../../utils/apis/account";

const ShareModal = ({ isOpen, onClose, certificateUrl, certificateTitle, certificateId }) => {
    const idToken = useSelector((state) => state.auth.idToken);
    const [loadingPlatform, setLoadingPlatform] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    // Get share URL from the backend API
    const getShareUrl = async () => {
        let shareUrlToUse = certificateUrl;
        if (certificateId) {
            try {
                const response = await shareCertificateRequest(idToken, certificateId);
                if (response && response.url) {
                    shareUrlToUse = response.url;
                }
            } catch (error) {
                console.error(error);
            }
        }
        return shareUrlToUse;
    };

    // Copy certificate link to clipboard
    const handleCopyLink = async () => {
        try {
            const shareUrl = await getShareUrl();
            await navigator.clipboard.writeText(shareUrl);
            setLinkCopied(true);
            message.success("Certificate link copied to clipboard!");
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (e) {
            message.error("Failed to copy link");
        }
    };

    // Download handler - simple direct download
    const handleDownload = () => {
        // Create a temporary link and trigger download
        const a = document.createElement("a");
        a.href = certificateUrl;
        a.download = `${certificateTitle || "Certificate"}.png`;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleShare = async (platform) => {
        setLoadingPlatform(platform);

        const shareUrlToUse = await getShareUrl();
        const shareText = encodeURIComponent(`Check out my ${certificateTitle} certificate!`);
        const shareUrl = encodeURIComponent(shareUrlToUse);

        switch (platform) {
            case "native":
                try {
                    let shared = false;
                    // First try to share the image directly to apps like Instagram
                    if (navigator.canShare) {
                        try {
                            message.loading({ content: "Preparing image for sharing...", key: "share_progress" });
                            
                            let blob;
                            try {
                                // Try to download the actual image to attach it
                                const response = await fetch(certificateUrl);
                                if (!response.ok) throw new Error("Direct fetch not OK");
                                blob = await response.blob();
                            } catch (directError) {
                                // If blocked by CORS, use a raw data proxy to bypass the restriction!
                                console.warn("Direct fetch blocked by CORS, trying proxy bypass...", directError);
                                const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(certificateUrl)}`;
                                const response = await fetch(proxyUrl);
                                blob = await response.blob();
                            }
                            
                            // Make sure we have a valid image type fallback
                            const fileType = blob.type.startsWith('image/') ? blob.type : 'image/png';
                            const file = new File([blob], "certificate.png", { type: fileType });
                            
                            // If browser allows sharing files, DO NOT pass 'url', just pass 'files' and 'text'. 
                            if (navigator.canShare({ files: [file] })) {
                                message.destroy("share_progress"); // Clear loading
                                await navigator.share({
                                    text: `Check out my ${certificateTitle} certificate! ${shareUrlToUse}`,
                                    files: [file]
                                });
                                shared = true;
                            }
                        } catch (e) {
                            console.warn("Could not fetch image for native share", e);
                            message.error({ content: "Could not grab actual image. Defaulting to link sharing.", key: "share_progress", duration: 3 });
                        }
                    }

                    // Fallback to simple link share if image share not possible
                    if (!shared && navigator.share) {
                        message.destroy("share_progress");
                        await navigator.share({
                            title: certificateTitle || 'Certificate',
                            text: `Check out my ${certificateTitle} certificate!`,
                            url: shareUrlToUse,
                        });
                    }
                } catch (error) {
                    // Ignore DOMException: The user aborted a request
                    message.destroy("share_progress");
                    if (error.name !== "AbortError") {
                        message.error("Failed to share via device apps");
                    }
                }
                break;

            case "twitter":
                // Open Twitter and trigger download
                window.open(
                    `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                // Trigger download after a short delay
                setTimeout(() => {
                    handleDownload();
                    message.info("Image download started! Attach it to your tweet.");
                }, 500);
                break;

            case "facebook":
                // Open Facebook and trigger download
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                setTimeout(() => {
                    handleDownload();
                    message.info("Image download started! Attach it to your post.");
                }, 500);
                break;

            case "instagram":
                // For Instagram, just download and copy link
                handleDownload();
                try {
                    await navigator.clipboard.writeText(shareUrlToUse);
                    message.success("Image download started & link copied! Share on Instagram.");
                } catch (e) {
                    message.info("Image download started! Share on Instagram.");
                }
                break;

            default:
                break;
        }

        setLoadingPlatform(null);
        onClose();
    };

    const platforms = [
        ...(navigator.share ? [{
            name: "Share via Apps",
            key: "native",
            icon: shareIcon,
            color: "#4A90E2",
        }] : []),
        {
            name: "X (Twitter)",
            key: "twitter",
            icon: twitterIcon,
            color: "#000",
        },
        {
            name: "Instagram",
            key: "instagram",
            icon: instagramIcon,
            gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
        },
        {
            name: "Facebook",
            key: "facebook",
            icon: facebookIcon,
            color: "#1877F2",
        },
        {
            name: "Testimonials",
            key: "testimonials",
            icon: starIcon,
            color: "#36d66b",
            disabled: true,
        },
    ];

    return (
        <Modal
            open={isOpen}
            onCancel={() => {
                setLinkCopied(false);
                setLoadingPlatform(null);
                onClose();
            }}
            footer={null}
            centered
            className="share-certificate-modal"
            width={440}
            style={{
                maxWidth: "calc(100vw - 32px)",
                top: 0
            }}
            styles={{
                body: { padding: 0 }
            }}
        >
            <div className="share-modal-content">
                <h2 className="share-modal-title">Share Certificate</h2>
                <p className="share-modal-subtitle">Share your achievement on social media</p>

                {/* Certificate image preview */}
                <div className="share-certificate-preview">
                    <img src={certificateUrl} alt="Certificate preview" />
                </div>

                {/* Action buttons */}
                <div className="share-action-buttons">
                    <button
                        className={`share-copy-link-btn ${linkCopied ? "copied" : ""}`}
                        onClick={handleCopyLink}
                    >
                        {linkCopied ? "✓ Copied!" : "📋 Copy Link"}
                    </button>
                    <button className="share-download-btn" onClick={handleDownload}>
                        ⬇ Download Image
                    </button>
                </div>

                <p className="share-helper-text">
                    Download the image and upload it when sharing for best results
                </p>

                <div className="share-platforms">
                    {platforms.map((platform) => (
                        <div
                            key={platform.key}
                            className={`share-platform-item ${platform.disabled || loadingPlatform === platform.key ? "disabled" : ""}`}
                            onClick={() =>
                                !platform.disabled && loadingPlatform !== platform.key && handleShare(platform.key)
                            }
                        >
                            <div
                                className="platform-icon"
                                style={{ background: platform.gradient || platform.color }}
                            >
                                <img src={platform.icon} alt={platform.name} />
                            </div>
                            <span className="platform-name">
                                {loadingPlatform === platform.key ? "Sharing..." : platform.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default ShareModal;
